import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Avatar,
  Divider,
  Paper,
  Chip,
  Grid,
  Stack,
  Link,
  CircularProgress,
} from "@mui/material";
import {
  Error as ErrorIcon,
  OpenInNew as OpenInNewIcon,
  Public as ChromeIcon,
  Apple as AppleIcon,
  CalendarToday,
  Flag,
  Label,
  Language,
  Timeline,
  AccessTime,
  Code,
  People,
  BarChart,
  Info,
  BugReport,
  Devices as DevicesIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useTranslation } from "react-i18next";
import { colorPalette, getInitials } from "../../utils";
import { useGetMultipleIpGeolocationsQuery } from "../../../../bug-tracker/api/ipGeoLocationApi";

dayjs.extend(relativeTime);

interface BugDetailTabsProps {
  bugDetailsData: any;
  bugIncident?: any[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bug-detail-tabpanel-${index}`}
      aria-labelledby={`bug-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface IpGeoData {
  lat: number;
  lon: number;
  city: string;
  country: string;
  regionName: string;
  isp: string;
  ipAddress: string;
}

const BugDetailTabs: React.FC<BugDetailTabsProps> = ({
  bugDetailsData,
  bugIncident = [],
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  // Extract unique IP addresses from bugIncident
  const uniqueIps = useMemo(() => {
    return Array.from(
      new Set(
        bugIncident
          .map((incident: any) => incident.ipAddress)
          .filter(Boolean),
      ),
    );
  }, [bugIncident]);

  // Use RTK Query to fetch geolocation data via proxy
  const {
    data: ipGeoData = [],
    isLoading: isLoadingGeo,
  } = useGetMultipleIpGeolocationsQuery(uniqueIps, {
    skip: uniqueIps.length === 0,
  });

  console.log(mapReady, "mapReadymapReady");

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const timeAgo = (date: string) => {
    return dayjs(date).fromNow();
  };

  // Parse deviceDetails JSON string
  const deviceDetails = useMemo(() => {
    if (!bugDetailsData?.deviceDetails) return null;
    try {
      return typeof bugDetailsData.deviceDetails === "string"
        ? JSON.parse(bugDetailsData.deviceDetails)
        : bugDetailsData.deviceDetails;
    } catch {
      return null;
    }
  }, [bugDetailsData?.deviceDetails]);

  // Parse autpBugData JSON string
  const autoBugData = useMemo(() => {
    if (!bugDetailsData?.autpBugData) return null;
    try {
      return typeof bugDetailsData.autpBugData === "string"
        ? JSON.parse(bugDetailsData.autpBugData)
        : bugDetailsData.autpBugData;
    } catch {
      return null;
    }
  }, [bugDetailsData?.autpBugData]);

  // console.log(bugDetailsData, "bugDetailsDatabugDetailsData");

  // Calculate map center and zoom based on all IP locations
  const calculateMapData = useMemo(() => {
    if (ipGeoData.length === 0) return null;

    if (ipGeoData.length === 1) {
      return {
        center: { lat: ipGeoData[0].lat, lon: ipGeoData[0].lon },
        zoom: 12,
      };
    }

    // Calculate bounding box
    const lats = ipGeoData.map((ip) => ip.lat);
    const lons = ipGeoData.map((ip) => ip.lon);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);

    // Calculate center
    const centerLat = (minLat + maxLat) / 2;
    const centerLon = (minLon + maxLon) / 2;

    // Calculate zoom level based on the distance
    const latDiff = maxLat - minLat;
    const lonDiff = maxLon - minLon;
    const maxDiff = Math.max(latDiff, lonDiff);

    // Approximate zoom calculation
    let zoom = 12;
    if (maxDiff > 50) zoom = 3;
    else if (maxDiff > 20) zoom = 5;
    else if (maxDiff > 10) zoom = 6;
    else if (maxDiff > 5) zoom = 7;
    else if (maxDiff > 2) zoom = 8;
    else if (maxDiff > 1) zoom = 9;
    else if (maxDiff > 0.5) zoom = 10;
    else if (maxDiff > 0.1) zoom = 11;

    return {
      center: { lat: centerLat, lon: centerLon },
      zoom,
      bounds: { minLat, maxLat, minLon, maxLon },
    };
  }, [ipGeoData]);

  // IP geolocation data is now fetched via RTK Query hook (using proxy API)

  // Initialize Leaflet map with markers
  const initializeMap = useCallback(() => {
    if (!mapRef.current || !calculateMapData || ipGeoData.length === 0) return;
    const L = (window as any).L;
    if (!L) return;

    // Clear existing map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    // Create map without default Leaflet attribution prefix
    const map = L.map(mapRef.current, {
      attributionControl: false,
    }).setView(
      [calculateMapData.center.lat, calculateMapData.center.lon],
      calculateMapData.zoom
    );
    mapInstanceRef.current = map;

    // Add custom attribution without Leaflet prefix
    L.control.attribution({ prefix: false }).addTo(map);

    // Add OpenStreetMap tile layer
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="#">Sniffer</a>',
    }).addTo(map);

    // Create bounds to fit all markers
    const bounds: [number, number][] = [];

    // Add markers for each IP location
    ipGeoData.forEach((ipData, index) => {
      const position: [number, number] = [ipData.lat, ipData.lon];
      bounds.push(position);

      // Create numbered marker icon
      const markerIcon = L.divIcon({
        className: "custom-marker",
        html: `<div style="
          background-color: ${index === 0 ? '#1976d2' : '#9c27b0'};
          color: white;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          border: 2px solid white;
          box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        ">${index + 1}</div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });

      const marker = L.marker(position, { icon: markerIcon }).addTo(map);

      // Add popup
      marker.bindPopup(`
        <div style="padding: 4px; min-width: 150px;">
          <strong style="color: #1976d2;">${ipData.ipAddress}</strong><br/>
          <span style="color: #666;">${ipData.city}, ${ipData.regionName}</span><br/>
          <span style="color: #666;">${ipData.country}</span><br/>
          <small style="color: #999;">ISP: ${ipData.isp}</small>
        </div>
      `);

      markersRef.current.push(marker);
    });

    // Fit map to show all markers if more than one
    if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [30, 30] });
    }

    // Fix white screen issue - invalidateSize after a short delay to ensure container is rendered
    setTimeout(() => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    }, 100);

    setMapReady(true);
  }, [calculateMapData, ipGeoData]);

  // Load Leaflet script and CSS, then initialize map when Location tab is active
  useEffect(() => {
    // Only initialize when Location tab (index 4) is active
    if (tabValue !== 4 || ipGeoData.length === 0 || !calculateMapData) return;

    let initTimeout: ReturnType<typeof setTimeout>;

    const loadLeaflet = () => {
      // Check if already loaded
      if ((window as any).L) {
        // Delay initialization to ensure container is rendered
        initTimeout = setTimeout(() => {
          initializeMap();
        }, 100);
        return;
      }

      // Load CSS
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const css = document.createElement("link");
        css.rel = "stylesheet";
        css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(css);
      }

      // Load JS
      const existingScript = document.querySelector('script[src*="leaflet.js"]');
      if (existingScript) {
        existingScript.addEventListener("load", () => {
          initTimeout = setTimeout(() => {
            initializeMap();
          }, 100);
        });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.async = true;
      script.onload = () => {
        initTimeout = setTimeout(() => {
          initializeMap();
        }, 100);
      };
      document.head.appendChild(script);
    };

    loadLeaflet();

    // Cleanup on unmount
    return () => {
      if (initTimeout) clearTimeout(initTimeout);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [tabValue, ipGeoData, calculateMapData, initializeMap]);

  return (
    <Box sx={{ bgcolor: "background.paper", mt: 2, mb: 8 }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          px: 2,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            value={0}
            icon={<ErrorIcon />}
            iconPosition="start"
            label={t("tabErrorDetails")}
            sx={{ textTransform: "none", bgcolor: theme.palette.mode === "dark" ? "rgba(255, 152, 0, 0.12)" : "#fff3e0" }}
          />
          <Tab
            value={1}
            icon={<Code />}
            iconPosition="start"
            label={t("tabPageContext")}
            sx={{ textTransform: "none" }}
          />
          <Tab
            value={2}
            icon={<BarChart />}
            iconPosition="start"
            label={t("tabPerformance")}
            sx={{ textTransform: "none" }}
          />

          <Tab
            value={3}
            icon={<BugReport />}
            iconPosition="start"
            label={t("tabOverview")}
            sx={{ textTransform: "none" }}
          />
          <Tab
            value={4}
            icon={<Language />}
            iconPosition="start"
            label={t("tabLocation")}
            sx={{ textTransform: "none" }}
          />
          <Tab
            value={5}
            icon={<BarChart />}
            iconPosition="start"
            label={t("tabStatistics")}
            sx={{ textTransform: "none" }}
          />
          <Tab
            value={6}
            icon={<People />}
            iconPosition="start"
            label={t("tabAssignees")}
            sx={{ textTransform: "none" }}
          />
          <Tab
            value={7}
            icon={<Info />}
            iconPosition="start"
            label={t("tabAdditional")}
            sx={{ textTransform: "none" }}
          />
          <Tab
            value={8}
            icon={<DevicesIcon />}
            iconPosition="start"
            label={t("tabDevice") || "Device"}
            sx={{ textTransform: "none" }}
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card variant="outlined" sx={{ borderLeft: "4px solid #f44336" }}>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <ErrorIcon color="error" />
              {t("consoleErrorDetails")}
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              {/* Error Message */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: theme.palette.mode === "dark" ? "rgba(244, 67, 54, 0.08)" : "#ffebee" }}>
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      color="error"
                      fontWeight={600}
                      gutterBottom
                    >
                      {t("errorMessage")}
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "background.paper",
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {autoBugData?.error?.console?.message || "N/A"}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Stack Trace */}
              {autoBugData?.error?.console?.stack && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        gutterBottom
                      >
                        {t("stackTrace")}
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "action.hover",
                          borderRadius: 1,
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowX: "auto",
                        }}
                      >
                        {autoBugData.error.console.stack}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Error Metadata */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("errorId")}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ fontFamily: "monospace" }}
                    >
                      {autoBugData?.error?.errorId || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("errorType")}
                    </Typography>
                    <Chip
                      label={autoBugData?.error?.errorType || "N/A"}
                      size="small"
                      color="error"
                      sx={{ ml: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("consoleLevel")}
                    </Typography>
                    <Chip
                      label={
                        autoBugData?.error?.console?.level?.toUpperCase() ||
                        "N/A"
                      }
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ ml: 0.5 }}
                    />
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("timestamp")}
                    </Typography>
                    <Typography variant="body2">
                      {autoBugData?.error?.timestamp
                        ? dayjs(autoBugData.error.timestamp).format(
                            "MMM DD, YYYY HH:mm:ss",
                          )
                        : "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("capturedAt")}
                    </Typography>
                    <Typography variant="body2">
                      {autoBugData?.error?.capturedAt
                        ? dayjs(autoBugData.error.capturedAt).format(
                            "MMM DD, YYYY HH:mm:ss",
                          )
                        : "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("errorUrl")}
                    </Typography>
                    {autoBugData?.error?.console?.url ? (
                      <Link
                        href={autoBugData.error.console.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          wordBreak: "break-all",
                          fontSize: "0.875rem",
                        }}
                      >
                        {autoBugData.error.console.url}
                      </Link>
                    ) : (
                      <Typography variant="body2">N/A</Typography>
                    )}
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 1: Page Context */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {/* Page Information */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t("pageInformation")}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("pageTitle")}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {autoBugData?.page?.title || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("url")}
                    </Typography>
                    {autoBugData?.page?.url ? (
                      <Link
                        href={autoBugData.page.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          wordBreak: "break-all",
                          fontSize: "0.875rem",
                        }}
                      >
                        {autoBugData.page.url}
                      </Link>
                    ) : (
                      <Typography variant="body2">N/A</Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("pathname")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {autoBugData?.page?.pathname || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("protocol")}
                    </Typography>
                    <Chip
                      label={autoBugData?.page?.protocol || "N/A"}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Document & Window */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t("documentAndWindow")}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("referrer")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: "break-all",
                        fontSize: "0.875rem",
                      }}
                    >
                      {autoBugData?.page?.referrer || t("none")}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("documentState")}
                    </Typography>
                    <Chip
                      label={autoBugData?.page?.documentReadyState || "N/A"}
                      size="small"
                      color={
                        autoBugData?.page?.documentReadyState === "complete"
                          ? "success"
                          : "warning"
                      }
                      sx={{ ml: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("windowSize")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                      <Chip
                        label={`W: ${autoBugData?.window?.innerWidth || "N/A"}px`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={`H: ${autoBugData?.window?.innerHeight || "N/A"}px`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("secureContext")}
                    </Typography>
                    <Chip
                      label={
                        autoBugData?.window?.isSecureContext
                          ? t("secure")
                          : t("notSecure")
                      }
                      size="small"
                      color={
                        autoBugData?.window?.isSecureContext
                          ? "success"
                          : "error"
                      }
                      sx={{ ml: 0.5 }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Network & Client Info */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t("networkInformation")}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t("clientIp")}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="primary"
                    >
                      {autoBugData?.clientIp || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t("connectionType")}
                    </Typography>
                    <Chip
                      label={
                        autoBugData?.networkInfo?.effectiveType?.toUpperCase() ||
                        "N/A"
                      }
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t("downlink")}
                    </Typography>
                    <Typography variant="body2">
                      {autoBugData?.networkInfo?.downlink || "N/A"} Mbps
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t("rtt")}
                    </Typography>
                    <Typography variant="body2">
                      {autoBugData?.networkInfo?.rtt || "N/A"} ms
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 2: Performance */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {/* Performance Metrics */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t("performanceMetrics")}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center", bgcolor: theme.palette.mode === "dark" ? "rgba(33, 150, 243, 0.08)" : "#e3f2fd" }}
                    >
                      <Typography variant="h4" color="primary" fontWeight={600}>
                        {Math.floor(autoBugData?.performance?.pageLoadTime || 0)}ms
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("pageLoadTime")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center", bgcolor: theme.palette.mode === "dark" ? "rgba(156, 39, 176, 0.08)" : "#f3e5f5" }}
                    >
                      <Typography
                        variant="h4"
                        // color="secondary"
                        fontWeight={600}
                      >
                        {Math.floor(autoBugData?.performance?.domContentLoaded || 0)}ms
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("domContentLoaded")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center", bgcolor: theme.palette.mode === "dark" ? "rgba(76, 175, 80, 0.08)" : "#e8f5e9" }}
                    >
                      <Typography
                        variant="h4"
                        color="success.main"
                        fontWeight={600}
                      >
                        {Math.floor(autoBugData?.performance?.firstContentfulPaint || 0)}ms
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("firstContentfulPaint")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center", bgcolor: theme.palette.mode === "dark" ? "rgba(255, 152, 0, 0.08)" : "#fff3e0" }}
                    >
                      <Typography
                        variant="h4"
                        color="warning.main"
                        fontWeight={600}
                      >
                        {Math.floor(autoBugData?.performance?.timeToInteractive || 0)}ms
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("timeToInteractive")}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Memory Usage */}
          {autoBugData?.performance?.memoryUsage && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t("memoryUsage")}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        {t("usedJsHeapSize")}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {(
                          (autoBugData.performance.memoryUsage.usedJSHeapSize ||
                            0) /
                          1024 /
                          1024
                        ).toFixed(2)}{" "}
                        MB
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        {t("totalJsHeapSize")}
                      </Typography>
                      <Typography variant="h6" >
                        {(
                          (autoBugData.performance.memoryUsage
                            .totalJSHeapSize || 0) /
                          1024 /
                          1024
                        ).toFixed(2)}{" "}
                        MB
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Frameworks Detected */}
          {autoBugData?.frameworks?.detected && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {t("detectedFrameworks")}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {autoBugData.frameworks.detected.map(
                      (framework: string, index: number) => (
                        <Chip
                          key={index}
                          label={framework}
                          color="primary"
                          variant="outlined"
                        />
                      ),
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Card variant="outlined">
          <CardContent>
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <ErrorIcon color="error" />
              {t("bugOverview")}
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Code fontSize="small" /> {t("bugKey")}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {bugDetailsData?.bugKey || "N/A"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Flag fontSize="small" /> {t("severity")}
                    </Typography>
                    <Chip
                      label={bugDetailsData?.severity || "N/A"}
                      size="small"
                      color={
                        bugDetailsData?.severity === "High"
                          ? "error"
                          : bugDetailsData?.severity === "Medium"
                            ? "warning"
                            : "default"
                      }
                      sx={{ mt: 0.5 }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Timeline fontSize="small" /> {t("status")}
                    </Typography>
                    <Chip
                      label={bugDetailsData?.bugStatus?.status || "N/A"}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Label fontSize="small" /> {t("tags")}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      {bugDetailsData?.tags &&
                      bugDetailsData.tags.length > 0 ? (
                        bugDetailsData.tags.map(
                          (tag: string, index: number) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ),
                        )
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t("noTags")}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <CalendarToday fontSize="small" /> {t("createdAt")}
                    </Typography>
                    <Typography variant="body1">
                      {bugDetailsData?.createdAt
                        ? dayjs(bugDetailsData.createdAt).format(
                            "MMM DD, YYYY HH:mm:ss",
                          )
                        : "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({timeAgo(bugDetailsData?.createdAt)})
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <AccessTime fontSize="small" /> {t("updatedAt")}
                    </Typography>
                    <Typography variant="body1">
                      {bugDetailsData?.updatedAt
                        ? dayjs(bugDetailsData.updatedAt).format(
                            "MMM DD, YYYY HH:mm:ss",
                          )
                        : "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({timeAgo(bugDetailsData?.updatedAt)})
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 2 or 6: Location */}
      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={2}>
          {/* Left Side - Location Details */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Language color="primary" />
                  {t("locationAndContext")}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("project")}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {bugDetailsData?.project?.name || "N/A"}
                    </Typography>
                    {/* <Typography variant="caption" color="text.secondary">
                      {t("boardType")}:{" "}
                      {bugDetailsData?.project?.boardType || "N/A"}
                    </Typography> */}
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <OpenInNewIcon fontSize="small" /> {t("pageUrl")}
                    </Typography>
                    {bugDetailsData?.pageUrl ? (
                      <Link
                        href={bugDetailsData.pageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {bugDetailsData.pageUrl}
                      </Link>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </Box>

                   {/* Location Details */}
                      <Box
                        sx={{
                          maxHeight: 300,
                          overflowY: "auto",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        <Stack spacing={1}>
                          {ipGeoData.map((ipData, index) => (
                            <Paper
                              key={index}
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                bgcolor: "action.hover",
                                borderLeft: "3px solid",
                                borderLeftColor:
                                  index === 0
                                    ? "primary.main"
                                    : "secondary.main",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "start",
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
                                  <Chip
                                    label={index + 1}
                                    size="small"
                                    color={
                                      index === 0 ? "primary" : "secondary"
                                    }
                                    sx={{ width: 28, height: 28 }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                    >
                                      {ipData.ipAddress}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="text.secondary"
                                    >
                                      {ipData.city}, {ipData.regionName},{" "}
                                      {ipData.country}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="text.secondary"
                                    >
                                      ISP: {ipData.isp}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ textAlign: "right" }}>
                                  <Chip
                                    label={`${ipData.lat.toFixed(4)}, ${ipData.lon.toFixed(4)}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Side - Google Map */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Language color="primary" />
                  {t("ipLocation") || "IP Location"}
                </Typography>
                <Divider sx={{ my: 2 }} />

                {isLoadingGeo ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : ipGeoData.length > 0 && calculateMapData ? (
                  <Box>
                    {/* Single Map with Multiple Markers */}
                    <Box
                      ref={mapRef}
                      sx={{
                        width: "100%",
                        height: 300,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                    {/* Marker Labels Overlay */}
                    {ipGeoData.length > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          mt: 1,
                          justifyContent: "center",
                        }}
                      >
                        {ipGeoData?.map((ipData, index) => (
                          <Chip
                            key={index}
                            icon={
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: "50%",
                                  bgcolor: "secondary.main",
                                  color: "white",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  ml: 0.5,
                                }}
                              >
                                {index + 1}
                              </Box>
                            }
                            label={`${ipData.city}, ${ipData.country}`}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              borderColor: index === 0 ? "primary.main" : "secondary.main",
                            }}
                          />
                        ))}
                      </Box>
                    )}

                    {/* Location Summary */}
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        gutterBottom
                      >
                        {t("Detected Locations") || "Detected Locations"} (
                        {ipGeoData.length})
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 2,
                        }}
                      >
                        {ipGeoData.map((ipData, index) => (
                          <Chip
                            key={index}
                            label={`${index + 1}. ${ipData.city}, ${ipData.country}`}
                            size="small"
                            // color={index === 0 ? "primary" : "secondary"}
                            variant="outlined"
                          />
                        ))}
                      </Box>

                     

                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                      bgcolor: "action.hover",
                      borderRadius: 2,
                    }}
                  >
                    <Typography color="text.secondary">
                      {bugIncident.length > 0
                        ? t("unableToFetchLocationData")
                        : t("noIpAddressAvailable")}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 3 or 7: Statistics */}
      <TabPanel value={tabValue} index={5}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t("statisticsAndCounts")}
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, textAlign: "center", bgcolor: "action.hover" }}
                >
                  <Typography variant="h4" color="primary" fontWeight={600}>
                    {bugDetailsData?.count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("totalCount")}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, textAlign: "center", bgcolor: "action.hover" }}
                >
                  <Typography variant="h4" color="error" fontWeight={600}>
                    {bugDetailsData?.autoBugCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("autoBugCount")}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, textAlign: "center", bgcolor: "action.hover" }}
                >
                  <Typography
                    variant="h4"
                    color="warning.main"
                    fontWeight={600}
                  >
                    {bugDetailsData?.relatedBugsCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("relatedBugs")}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, textAlign: "center", bgcolor: "action.hover" }}
                >
                  <Typography variant="h4" color="info.main" fontWeight={600}>
                    {bugDetailsData?.commentCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("comments")}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, textAlign: "center", bgcolor: "action.hover" }}
                >
                  <Typography variant="h4" fontWeight={600}>
                    {bugDetailsData?.subTasksCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("subTasks")}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, textAlign: "center", bgcolor: "action.hover" }}
                >
                  <Typography variant="h4" fontWeight={600}>
                    {bugDetailsData?.linkedBugsCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("linkedBugs")}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Logs Section */}
            {bugDetailsData?.logs && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t("logs")}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("consoleLogs")}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {bugDetailsData.logs.console || t("noLogs")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("networkLogs")}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {bugDetailsData.logs.network || t("noLogs")}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("actionLogs")}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {bugDetailsData.logs.actions || t("noLogs")}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 5 or 9: Assignees */}
      <TabPanel value={tabValue} index={6}>
        {bugDetailsData?.assignTo && bugDetailsData.assignTo.length > 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t("assignedTeamMembers")}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Stack spacing={1}>
                {bugDetailsData.assignTo.map((user: any, index: number) => (
                  <Paper
                    key={user._id || index}
                    variant="outlined"
                    sx={{ p: 2, bgcolor: "action.hover" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          backgroundColor:
                            colorPalette[index % colorPalette.length],
                          width: 40,
                          height: 40,
                        }}
                      >
                        {getInitials(user.fullName || "")}
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {user.fullName || t("unknown")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email || t("noEmail")}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outlined">
            <CardContent>
              <Typography
                variant="body1"
                color="text.secondary"
                textAlign="center"
              >
                {t("noAssigneesYet")}
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      {/* Tab 7 or 11: Additional */}
      <TabPanel value={tabValue} index={7}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t("additionalInformation")}
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t("dueDate")}
                </Typography>
                <Typography variant="body2">
                  {bugDetailsData?.dueDate
                    ? dayjs(bugDetailsData.dueDate).format("MMM DD, YYYY")
                    : t("notSet")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t("startDate")}
                </Typography>
                <Typography variant="body2">
                  {bugDetailsData?.startDate || t("notSet")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t("estimation")}
                </Typography>
                <Typography variant="body2">
                  {bugDetailsData?.estimation || t("notSet")}
                  {bugDetailsData?.estimationInSeconds
                    ? ` (${bugDetailsData.estimationInSeconds}s)`
                    : ""}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t("isSnoozed")}
                </Typography>
                <Chip
                  label={bugDetailsData?.isSnoozed ? t("yes") : t("no")}
                  size="small"
                  color={bugDetailsData?.isSnoozed ? "warning" : "success"}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t("isBacklog")}
                </Typography>
                <Chip
                  label={bugDetailsData?.isBackLog ? t("yes") : t("no")}
                  size="small"
                  color={bugDetailsData?.isBackLog ? "info" : "default"}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t("isRecurring")}
                </Typography>
                <Chip
                  label={bugDetailsData?.isRecurring ? t("yes") : t("no")}
                  size="small"
                  color={bugDetailsData?.isRecurring ? "error" : "default"}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t("boardIndex")}
                </Typography>
                <Typography variant="body2">
                  {bugDetailsData?.boardIndex ?? "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  {t("rank")}
                </Typography>
                <Typography variant="body2">
                  {bugDetailsData?.rank || "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 8 or 12: Device */}
      <TabPanel value={tabValue} index={8}>
        <Card variant="outlined">
          <CardContent>
            <Typography
              variant="h6"
              fontWeight={600}
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <DevicesIcon color="primary" />
              {t("deviceInfo") || "Device Information"}
            </Typography>
            <Divider sx={{ my: 2 }} />

            {deviceDetails ? (
              <Grid container spacing={3}>
                {/* Browser & OS */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                      >
                        {t("browserAndOperatingSystem")}
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("browser")}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mt: 0.5,
                              alignItems: "center",
                            }}
                          >
                            <ChromeIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {deviceDetails?.browser?.name || "N/A"}{" "}
                              {deviceDetails?.browser?.version || ""}
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("operatingSystem")}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mt: 0.5,
                              alignItems: "center",
                            }}
                          >
                            <AppleIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {deviceDetails?.operatingSystem?.name || "N/A"}{" "}
                              {deviceDetails?.operatingSystem?.version || ""}
                            </Typography>
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("userAgent")}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ wordBreak: "break-all", fontSize: "0.75rem" }}
                          >
                            {deviceDetails?.browser?.userAgent || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Screen Info */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                      >
                        {t("screen")}
                      </Typography>
                      <Stack spacing={2}>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          <Chip
                            label={`${t("resolution")}: ${deviceDetails?.screen?.resolution || "N/A"}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`${t("window")}: ${deviceDetails?.screen?.windowSize || "N/A"}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          <Chip
                            label={`${t("colorDepth")}: ${deviceDetails?.screen?.colorDepth || "N/A"}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`${t("pixelRatio")}: ${deviceDetails?.screen?.pixelRatio || "N/A"}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`${t("orientation")}: ${deviceDetails?.screen?.orientation || "N/A"}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Hardware Info */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                      >
                        {t("hardware")}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Chip
                          label={`${t("cpuCores")}: ${deviceDetails?.hardware?.cpuCores || "N/A"}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${t("memory")}: ${deviceDetails?.hardware?.memory || "N/A"}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${t("architecture")}: ${deviceDetails?.hardware?.architecture || "N/A"}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={
                            deviceDetails?.hardware?.touchSupport
                              ? t("touchEnabled")
                              : t("noTouch")
                          }
                          size="small"
                          variant="outlined"
                          color={
                            deviceDetails?.hardware?.touchSupport
                              ? "primary"
                              : "default"
                          }
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Network Info */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                      >
                        {t("network")}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Chip
                          label={`${t("connection")}: ${deviceDetails?.network?.connectionType?.toUpperCase() || "N/A"}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${t("downlink")}: ${deviceDetails?.network?.downlink || "N/A"} Mbps`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={
                            deviceDetails?.network?.online
                              ? t("online")
                              : t("offline")
                          }
                          size="small"
                          variant="outlined"
                          color={
                            deviceDetails?.network?.online ? "success" : "error"
                          }
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Locale Info */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                      >
                        {t("locale")}
                      </Typography>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("language")}
                          </Typography>
                          <Typography variant="body2">
                            {deviceDetails?.locale?.language || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("languages")}
                          </Typography>
                          <Typography variant="body2">
                            {deviceDetails?.locale?.languages || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("timezone")}
                          </Typography>
                          <Typography variant="body2">
                            {deviceDetails?.locale?.timeZone || "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Additional Info */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                      >
                        {t("additional")}
                      </Typography>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("cookiesEnabled")}
                          </Typography>
                          <Chip
                            label={deviceDetails?.cookies ? t("yes") : t("no")}
                            size="small"
                            variant="outlined"
                            color={
                              deviceDetails?.cookies ? "success" : "default"
                            }
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("source")}
                          </Typography>
                          <Typography variant="body2">
                            {deviceDetails?.source || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t("timestamp")}
                          </Typography>
                          <Typography variant="body2">
                            {deviceDetails?.timestamp
                              ? dayjs(deviceDetails.timestamp).format(
                                  "DD MMM YYYY, HH:mm:ss",
                                )
                              : "N/A"}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Typography color="text.secondary" textAlign="center" py={4}>
                {t("noDeviceInformationAvailable")}
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
};

export default BugDetailTabs;
